import * as amplitude from "@amplitude/analytics-browser";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Flex,
  HStack,
  Tag,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { FieldMetaProps, Form, Formik } from "formik";
import { noop } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { RiCheckLine, RiFlaskLine } from "react-icons/ri";
import { FormMustacheInput } from "src/components/Inputs/FormMustacheInput";
import { PaginatedTabList } from "src/components/Layout/PaginatedTabList";
import { ResizableDrawer } from "src/components/Layout/ResizableDrawer";
import FormikLexicalEditor from "src/components/WysiwygEditor/lexical/FormikEditor";
import { MessageContent, PreviewDrawerFormType } from "../schemas";
import {
  emailEditorSettings,
  smsEditorSettings,
} from "./lexicalEditorSettings";

import { FiAlertCircle } from "react-icons/fi";
import { MessageContextSchema } from "src/generated/hooks/avela-ai/requests";

type TranslationPreviewDrawerProps = {
  isOpen: boolean;
  translations: MessageContent[];
  receiversInfosByLanguage: Record<string, unknown[]>;
  hasRecipientsWithEmail: boolean;
  hasRecipientsWithPhone: boolean;
  onConfirm: (translations: MessageContent[]) => void;
  onCancel: () => void;
  messageContext?: MessageContextSchema;
};

export function TranslationPreviewDrawer({
  isOpen,
  translations,
  receiversInfosByLanguage,
  hasRecipientsWithEmail,
  hasRecipientsWithPhone,
  onConfirm,
  onCancel,
  messageContext,
}: TranslationPreviewDrawerProps) {
  const [currentLanguageIndex, setCurrentLanguageIndex] = useState(0);

  // Reset to first tab when drawer opens
  useEffect(() => {
    if (isOpen) {
      setCurrentLanguageIndex(0);
    }
  }, [isOpen]);

  const initialValues: PreviewDrawerFormType = useMemo(
    () => ({ messages: translations }),
    [translations]
  );

  const handleConfirm = useCallback(
    (
      values: PreviewDrawerFormType,
      getFieldMeta: (name: string) => FieldMetaProps<PreviewDrawerFormType>
    ) => {
      const messages = values.messages;
      signalMessageTranslationEdited(messages, getFieldMeta, messageContext);
      onConfirm(messages);
    },
    [onConfirm, messageContext]
  );

  const handleCancel = useCallback(
    (
      values: PreviewDrawerFormType,
      getFieldMeta: (name: string) => FieldMetaProps<PreviewDrawerFormType>
    ) => {
      const messages = values.messages;
      signalMessageTranslationEdited(messages, getFieldMeta, messageContext);
      onCancel();
    },
    [onCancel, messageContext]
  );

  return (
    <Formik<PreviewDrawerFormType>
      initialValues={initialValues}
      enableReinitialize
      onSubmit={noop}
    >
      {({ values, getFieldMeta }) => (
        <ResizableDrawer
          isOpen={isOpen}
          onClose={onCancel}
          closeOnEsc={false}
          closeOnOverlayClick={false}
          onCloseButtonClick={() => handleCancel(values, getFieldMeta)}
          autosaveId="translation-preview-drawer"
          contentProps={{
            bg: "white",
            minW: "32rem",
          }}
          header={
            <Flex direction="column" gap={1}>
              <Text fontSize="lg" fontWeight="400">
                Translate with Avela Intelligence
              </Text>
              <Box>
                <Tag size="sm" variant="outline" colorScheme="purple" gap={1}>
                  <RiFlaskLine />
                  <Text>Beta</Text>
                </Tag>
              </Box>
            </Flex>
          }
          footer={
            <Flex direction="column" gap={2} width="100%">
              <Text fontSize="xs" color="gray.600" textAlign="center">
                Avela Intelligence can make mistakes. Review important
                information.
              </Text>
              <Flex direction="row" gap={3} justify="flex-end">
                <Button
                  flex={1}
                  size="sm"
                  variant="outline"
                  colorScheme="gray"
                  onClick={() => handleCancel(values, getFieldMeta)}
                >
                  Cancel
                </Button>
                <Button
                  flex={1}
                  size="sm"
                  colorScheme="primary"
                  leftIcon={<RiCheckLine />}
                  onClick={() => handleConfirm(values, getFieldMeta)}
                >
                  Confirm translations
                </Button>
              </Flex>
            </Flex>
          }
        >
          <Form>
            <VStack spacing={6} align="stretch" py={4}>
              <Flex direction="column">
                {values.messages.length > 1 && (
                  <PaginatedTabList
                    items={values.messages.map((m) => {
                      const languageKey = m.language ?? m.language_name;
                      const receiverCount =
                        receiversInfosByLanguage[languageKey]?.length ?? 0;
                      return {
                        label: `${m.language_name} (${receiverCount})`,
                        value: languageKey,
                        language_name: m.language_name,
                        receiver_count: receiverCount,
                        hasError: !!m.error_message,
                      };
                    })}
                    currentIndex={currentLanguageIndex}
                    onTabChange={setCurrentLanguageIndex}
                    tabsProps={{
                      overflowX: "auto",
                      overflowY: "hidden",
                    }}
                    renderTab={({
                      language_name,
                      receiver_count,
                      hasError,
                    }) => {
                      return (
                        <HStack spacing={1} align="center" whiteSpace="nowrap">
                          {hasError && (
                            <Tooltip label="Unable to automatically translate">
                              <Box display="inline-flex">
                                <FiAlertCircle color="red" />
                              </Box>
                            </Tooltip>
                          )}
                          <Text>{language_name}</Text>
                          <Tooltip
                            label={`${receiver_count} recipient${
                              receiver_count !== 1 ? "s" : ""
                            } prefer ${language_name}`}
                          >
                            <Text color="blackAlpha.500">
                              ({receiver_count})
                            </Text>
                          </Tooltip>
                        </HStack>
                      );
                    }}
                  />
                )}
              </Flex>
              <Flex direction="column" gap={8}>
                {values.messages[currentLanguageIndex]?.error_message && (
                  <Alert
                    status="error"
                    as={Flex}
                    direction="row"
                    gap={2}
                    alignContent="flex-start"
                  >
                    <AlertIcon />
                    <Flex direction="column" gap={1}>
                      <AlertTitle>Unable to automatically translate</AlertTitle>
                      <AlertDescription>
                        {values.messages[currentLanguageIndex].error_message}
                      </AlertDescription>
                    </Flex>
                  </Alert>
                )}
                {hasRecipientsWithEmail ? (
                  <Flex direction="column" gap={2}>
                    <FormMustacheInput
                      name={`messages.${currentLanguageIndex}.subject`}
                      id={`messages.${currentLanguageIndex}.subject`}
                      label="Subject line"
                      formLabelProps={{ fontSize: "sm", color: "gray.700" }}
                    />
                    <FormikLexicalEditor
                      name={`messages.${currentLanguageIndex}.email_lexical`}
                      placeholder="Email content"
                      height="300px"
                      width="100%"
                      label="Email content"
                      labelProps={{ fontSize: "sm", color: "gray.700" }}
                      isRequired={true}
                      settings={emailEditorSettings}
                    />
                  </Flex>
                ) : (
                  <Text color="blackAlpha.700" fontWeight="400">
                    No email recipients
                  </Text>
                )}

                {hasRecipientsWithPhone ? (
                  <FormikLexicalEditor
                    name={`messages.${currentLanguageIndex}.sms_lexical`}
                    placeholder="SMS content"
                    height="150px"
                    width="100%"
                    editorVersion="lite"
                    label="SMS content"
                    labelProps={{ fontSize: "sm", color: "gray.700" }}
                    isRequired={true}
                    settings={smsEditorSettings}
                  />
                ) : (
                  <Text color="blackAlpha.700" fontWeight="400">
                    No SMS recipients
                  </Text>
                )}
              </Flex>
            </VStack>
          </Form>
        </ResizableDrawer>
      )}
    </Formik>
  );
}

function signalMessageTranslationEdited(
  messages: MessageContent[],
  getFieldMeta: (name: string) => FieldMetaProps<PreviewDrawerFormType>,
  messageContext?: MessageContextSchema
) {
  for (const [index, message] of messages.entries()) {
    const isEmailLexicalEdited = getFieldMeta(
      `messages.${index}.email_lexical`
    ).touched;
    const isSmsLexicalEdited = getFieldMeta(
      `messages.${index}.sms_lexical`
    ).touched;

    if (isEmailLexicalEdited || isSmsLexicalEdited) {
      amplitude.track("Edited adhoc translation", {
        ...messageContext,
        language: message.language,
        email_content_edited: isEmailLexicalEdited,
        sms_content_edited: isSmsLexicalEdited,
      });
    }
  }
}
